import { Factory } from "@/data/dao/Factory";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { TableDao } from "@/data/dao/service/TableDao";
import type { IAllocationHistory } from "@/data/model/Table";
import { role } from "@/data/util/Config";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";

import { i18n, isInRole } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { AllocationHistoryExportWindow } from "@/portal/modal/AllocationHistoryExportWindow";
import { getLocationName } from "@/shared/helpers/LocationHelper";

export class AllocationHistoryTableController {
    private $element: JQuery;
    private dao: TableDao;

    private dataTable: DataTable<IAllocationHistory>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<TableDao>(TableDao);

        //bind export btn
        this.$element.find("> .kt-portlet__head > .kt-portlet__head-toolbar .btn.export").click((e) => {
            e.preventDefault();
            e.stopPropagation();

            const wi = new AllocationHistoryExportWindow();
            wi.show();
        });

        const tableConfig: IDataTableConfig<IAllocationHistory> = {
            columnMapping: [
                {
                    name: i18n("allocationHistory.fields.allocated"),
                    property: "allocated",
                    styles: { width: "8%" },
                    formatter: (m, p) => DateTime.toPreciseDateTimeString(p),
                },
                {
                    name: i18n("allocationHistory.fields.deallocated"),
                    property: "deallocated",
                    styles: { width: "7%" },
                    formatter: (m, p) =>
                        p === undefined ? i18n("general.timeFormat.notYet") : DateTime.toPreciseDateTimeString(p),
                },
                {
                    name: i18n("general.entities.location"),
                    property: "locationName",
                    styles: { width: "12%" },
                    formatter: (m) => {
                        if (isInRole(role.Admin) || isInRole(role.Marketing) || isInRole(role.Manager))
                            return (
                                '<a href="/location/' + m.locationId + '">' + Format.htmlEscape(m.locationName) + "</a>"
                            );
                        else return Format.htmlEscape(m.locationName);
                    },
                },
                {
                    name: i18n("general.entities.table"),
                    property: "tableName",
                    styles: { width: "7%" },
                    formatter: (m, p) => {
                        if (p === undefined) return "-";

                        if (isInRole(role.Admin) || isInRole(role.Manager))
                            return '<a href="/table/' + m.tableId + '">' + Format.htmlEscape(m.tableName) + "</a>";
                        else return Format.htmlEscape(m.tableName);
                    },
                },
                {
                    name: i18n("allocationHistory.fields.additionalInfo"),
                    property: "tableAdditionalInfo",
                    styles: { width: "6%" },
                },
                {
                    name: i18n("general.fields.firstName"),
                    property: "firstName",
                    styles: { width: "6%" },
                },
                {
                    name: i18n("general.fields.lastName"),
                    property: "lastName",
                    styles: { width: "6%" },
                },
                {
                    name: i18n("allocationHistory.fields.zip"),
                    property: "zip",
                    styles: { width: "5%" },
                    formatter: (m, p) => (p !== undefined ? Format.htmlEscape(p) : ""),
                },
                {
                    name: i18n("general.fields.birthday"),
                    property: "birthday",
                    styles: { width: "5%" },
                    formatter: (m, p) => (p !== undefined ? DateTime.toDateString(p) : ""),
                },
                {
                    name: i18n("allocationHistory.fields.phone"),
                    property: "phoneNumber",
                    styles: { width: "7%" },
                },
                {
                    name: i18n("allocationHistory.fields.isSignedUp"),
                    property: "isSignedUp",
                    styles: { width: "6%" },
                    formatter: (m, p) => (p ? i18n("general.yes") : i18n("general.no")),
                },
                {
                    name: i18n("allocationHistory.fields.isValid"),
                    property: "isCompleteCovidSignup",
                    styles: { width: "8%" },
                    formatter: (m, p) => (p ? i18n("general.yes") : i18n("general.no")),
                },
            ],
            defaultSortAsc: false,
            defaultSortProperty: "allocated",
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.search.addDropdownFilter(
            "locationId",
            Factory.getInstance<LocationDao>(LocationDao),
            false,
            i18n("allocationHistory.allLocations"),
            undefined,
            (e) => { return getLocationName(e) } 
        );

        this.dataTable = new DataTable<IAllocationHistory>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao.allocationHistoryDataSource,
            tableConfig
        );
        this.dataTable.listenTo(this.search);
    }
}
